import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getBatchConfig } from '../config/batchConfig';

const ProcessingSnackbarContext = createContext();
const STORAGE_KEY = 'processing_batches_state';

const serializeBatches = (batches) => {
  const obj = {};
  batches.forEach((batch, batchId) => {
    obj[batchId] = batch;
  });
  return JSON.stringify(obj);
};

const deserializeBatches = (json) => {
  try {
    const obj = JSON.parse(json || '{}');
    const map = new Map();
    Object.entries(obj).forEach(([batchId, batch]) => {
      map.set(batchId, batch);
    });
    return map;
  } catch (_) {
    return new Map();
  }
};

export const ProcessingSnackbarProvider = ({ children }) => {
  const [batches, setBatches] = useState(() => {
    if (typeof window === 'undefined') return new Map();
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return deserializeBatches(saved);
    } catch (_) {
      return new Map();
    }
  });

  // Persist batches to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, serializeBatches(batches));
  }, [batches]);

  // Auto-cleanup: Remove completed batches based on config
  useEffect(() => {
    const interval = setInterval(() => {
      setBatches((prev) => {
        const now = new Date();
        const nextBatches = new Map(prev);
        let changed = false;

        nextBatches.forEach((batch, batchId) => {
          if (batch.uploadStep === 'complete' && batch.createdAt) {
            const config = getBatchConfig(batch.batchType);
            const createdTime = new Date(batch.createdAt);
            const ageMs = now - createdTime;

            // markUploadStepCompleted(batchId);

            // Auto-remove if configured or if older than configured duration
            if (ageMs > config.autoRemoveCompletedAfterMs) {
              nextBatches.delete(batchId);
              changed = true;
            }
          }
        });

        return changed ? nextBatches : prev;
      });
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(interval);
  }, []);

  const addBatch = useCallback((batchId, batchData) => {
    setBatches((prev) => {
      const next = new Map(prev);
      next.set(batchId, {
        batchId,
        batchType: null,
        uploadStep: null,
        uploadedFileNames: [],
        selectedFilesCount: 0,
        notifyOnComplete: false,
        notified: false,
        collapsed: false,
        createdAt: new Date().toISOString(),
        ...batchData,
      });
      return next;
    });
  }, []);

  const updateBatch = useCallback((batchId, updates) => {
    setBatches((prev) => {
      const next = new Map(prev);
      const existing = next.get(batchId);
      if (existing) {
        next.set(batchId, { ...existing, ...updates });
      }
      return next;
    });
  }, []);

  const removeBatch = useCallback((batchId) => {
    setBatches((prev) => {
      const next = new Map(prev);
      next.delete(batchId);
      return next;
    });
  }, []);

  const clearAllBatches = useCallback(() => {
    setBatches(new Map());
  }, []);

  const clearCompletedBatches = useCallback(() => {
    setBatches((prev) => {
      const next = new Map(prev);
      const toRemove = [];
      next.forEach((batch, batchId) => {
        if (batch.uploadStep === 'complete') {
          toRemove.push(batchId);
        }
      });
      toRemove.forEach((batchId) => next.delete(batchId));
      return toRemove.length > 0 ? next : prev;
    });
  }, []);

  const toggleBatchCollapsed = useCallback((batchId, collapsed) => {
    // Toggle is now handled locally in ProcessingSnackbar component
  }, []);

  const markUploadStepCompleted = useCallback((batchId) => {
    updateBatch(batchId, { uploadStep: 'complete' });
  }, [updateBatch]);

  const markBatchNotified = useCallback((batchId) => {
    updateBatch(batchId, { notified: true });
  }, [updateBatch]);

  const getIsShortlisting = useCallback(() => {
    return Array.from(batches.values()).some(b => b.batchType === 'dealflow_shortlist' && b.uploadStep === 'processing');
  }, [batches]);

  const value = React.useMemo(() => ({
    batches,
    addBatch,
    updateBatch,
    removeBatch,
    clearAllBatches,
    clearCompletedBatches,
    toggleBatchCollapsed,
    markUploadStepCompleted,
    markBatchNotified,
    getIsShortlisting,
  }), [batches, addBatch, updateBatch, removeBatch, clearAllBatches, clearCompletedBatches, toggleBatchCollapsed, markUploadStepCompleted, markBatchNotified, getIsShortlisting]);

  return (
    <ProcessingSnackbarContext.Provider value={value}>
      {children}
    </ProcessingSnackbarContext.Provider>
  );
};

export const useProcessingSnackbar = () => {
  const context = useContext(ProcessingSnackbarContext);
  if (!context) {
    throw new Error('useProcessingSnackbar must be used within ProcessingSnackbarProvider');
  }
  return context;
};
